import { ListChecksIcon, ListXIcon, WandSparklesIcon } from "lucide-react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { ObjectTypeFragment } from "@/apollo/types";
import {
  Button,
  ButtonGroup,
  ButtonPrimitive,
} from "@/components/elements/Button";
import confirm from "@/components/elements/Confirm";
import Tooltip from "@/components/elements/Tooltip";
import FieldLabel from "@/components/primitives/InputLabel";
import cn from "@/helpers/classNames";
import { PlusIcon } from "@heroicons/react/16/solid";
import { ArrowDownIcon, MinusCircleIcon } from "@heroicons/react/24/solid";

import { useMappingDataContext } from "../../MappingDataContext";
import { FormValues } from "../../form";
import { DestinationPropertySelector } from "../components/DestinationPropertySelector";
import { FieldErrorMessage } from "../components/FieldErrorMessage";
import { FieldMappingSkeleton } from "../components/FieldMappingSkeleton";
import { MappingGrid, MappingGridHeader } from "../components/MappingGrid";
import { SourcePropertySelector } from "../components/SourcePropertySelector";

export function FieldMappingSection(props: {
  modelName: string;
  objectType: ObjectTypeFragment;
  destinationIntegrationId: string;
}) {
  const formMethods = useFormContext<FormValues>();
  const supportsCustomProperties =
    props.objectType.supportedDestinationMapping.includes("custom");

  const {
    sourceProperties: {
      all: allSourceProperties,
      available: availableSourceProperties,
    },
    destinationProperties: {
      allWritable: allDestinationProperties,
      availableWritable: availableDestinationProperties,
    },
    refetchDestinationProperties,
    refetchSourceProperties,
    autoMapFields,
    isLoadingDestinationProperties,
    isLoadingSourceProperties,
    autoMappingSuggestions,
  } = useMappingDataContext();

  const { fields, remove, replace } = useFieldArray({
    control: formMethods.control,
    name: "mappings",
  });

  const handleAddField = () => {
    const mappings = formMethods.getValues("mappings");
    const nextSourceProperty = availableSourceProperties.at(0);
    if (nextSourceProperty == null) {
      return;
    }
    replace(
      mappings
        .filter(
          (x) =>
            x.sourceProperty != null || x.destinationProperty?.meta?.isRequired,
        )
        .concat({
          destinationProperty: null,
          sourceProperty: nextSourceProperty,
        }),
    );
  };

  const handleAddRemainingSourceProperties = () => {
    const mappings = formMethods.getValues("mappings");
    replace(
      mappings
        .filter(
          (x) =>
            x.sourceProperty != null || x.destinationProperty?.meta?.isRequired,
        )
        .concat(
          availableSourceProperties.map((prop) => ({
            destinationProperty: null,
            sourceProperty: prop,
          })),
        ),
    );
  };

  function clearMappings() {
    const mappings = formMethods
      .getValues("mappings")
      // Keep only required mappings
      .filter((x) => x.isRequired);
    for (const item of mappings) {
      // Clear source property on required mappings
      item.sourceProperty = null;
    }
    if (mappings.length === 0) {
      mappings.push({
        sourceProperty: null,
        destinationProperty: null,
      });
    }
    replace(mappings);
  }

  const canClearMappings = () => {
    const mappings = formMethods.getValues("mappings");
    return mappings.some((m) => {
      const { sourceProperty, destinationProperty } = m;
      if (destinationProperty?.meta?.isRequired) {
        return sourceProperty != null;
      }
      return sourceProperty != null || destinationProperty != null;
    });
  };

  return (
    <>
      <div>
        <FieldLabel className="leading-normal">Field Mapping</FieldLabel>
        <p className="text-xs text-muted-foreground">
          Configure how the columns in your source model should be mapped to
          fields in <strong>{props.objectType.objectName}</strong>.
        </p>
      </div>

      <div className="mt-4">
        <MappingGridHeader
          destinationIntegrationId={props.destinationIntegrationId}
          isLoadingSourceProperties={isLoadingSourceProperties}
          isLoadingDestinationProperties={isLoadingDestinationProperties}
          onRefreshSourceProperties={refetchSourceProperties}
          onRefreshDestinationProperties={refetchDestinationProperties}
        />
        <div className="mt-2 space-y-8 sm:space-y-4">
          {fields.length === 0 &&
            (isLoadingDestinationProperties || isLoadingSourceProperties) && (
              <FieldMappingSkeleton rows={3} />
            )}

          {fields.map((item, index) => (
            <div className="relative" key={item.id}>
              <MappingGrid>
                <div className="min-w-0">
                  <Controller<FormValues, `mappings.${number}.sourceProperty`>
                    name={`mappings.${index}.sourceProperty`}
                    render={({ field, fieldState }) => {
                      const value = field.value;
                      const options = (value ? [value] : []).concat(
                        availableSourceProperties,
                      );
                      const error = fieldState.error?.message;
                      return (
                        <SourcePropertySelector
                          {...field}
                          options={options}
                          className={cn({
                            "rounded-sm outline outline-2 outline-offset-2 outline-red-500":
                              !!error,
                          })}
                        />
                      );
                    }}
                    rules={{
                      validate: (value) => {
                        if (value == null) {
                          return "Source property is required";
                        }
                        if (
                          allSourceProperties.find(
                            (x) => x.name === value.name,
                          ) == null
                        ) {
                          return `"${value.name}" does not exist in ${props.modelName}`;
                        }
                        return true;
                      },
                    }}
                  />
                  <FieldErrorMessage<FormValues>
                    name={`mappings.${index}.sourceProperty`}
                  />
                </div>
                <div className="flex flex-none items-center justify-center">
                  <ArrowDownIcon className="h-4 w-4 text-muted-foreground sm:mt-2.5 sm:h-5 sm:w-5 sm:-rotate-90" />
                </div>
                <div className="min-w-0">
                  <Controller<
                    FormValues,
                    `mappings.${number}.destinationProperty`
                  >
                    name={`mappings.${index}.destinationProperty`}
                    render={({ field, fieldState }) => {
                      const value = field.value;
                      const error = fieldState.error?.message;
                      const isRequired = formMethods.getValues(
                        `mappings.${index}.isRequired`,
                      );
                      return (
                        <DestinationPropertySelector
                          {...field}
                          value={value ?? undefined}
                          nullable
                          options={availableDestinationProperties}
                          className={cn({
                            "rounded-sm outline outline-2 outline-offset-2 outline-red-500":
                              !!error,
                          })}
                          disabled={isRequired}
                          allowCreate={supportsCustomProperties}
                        />
                      );
                    }}
                    rules={{
                      validate: (value) => {
                        if (value == null) {
                          return "Destination property is required";
                        }
                        if (value.isCustom && !supportsCustomProperties) {
                          return `Custom properties are not supported for this object type`;
                        }
                        if (
                          allDestinationProperties.find(
                            (x) => x.propertyId === value.value,
                          ) == null
                        ) {
                          return `"${value.label}" does not exist in "${props.objectType.objectName}"`;
                        }
                        return true;
                      },
                    }}
                  />
                  <FieldErrorMessage<FormValues>
                    name={`mappings.${index}.destinationProperty`}
                  />
                </div>
              </MappingGrid>

              <Controller
                name={`mappings.${index}.isRequired`}
                render={({ field }) => {
                  if (field.value) {
                    return <></>;
                  }
                  return (
                    <Tooltip content="Remove this mapping">
                      <ButtonPrimitive
                        type="button"
                        onClick={() => {
                          if (fields.length < 2) {
                            return;
                          }
                          remove(index);
                        }}
                        className={cn(
                          "absolute right-full top-1/2 mr-2 box-content h-5 w-5 -translate-y-1/2 rounded-full p-0.5 text-foreground/25 hover:text-red-400",
                          {
                            invisible: fields.length < 2,
                          },
                        )}
                      >
                        <MinusCircleIcon />
                      </ButtonPrimitive>
                    </Tooltip>
                  );
                }}
              />
            </div>
          ))}
        </div>

        <ButtonGroup
          size="sm"
          className="mt-8 flex flex-wrap justify-between gap-4"
        >
          <div className="space-x-4">
            <Button
              icon={<PlusIcon />}
              isDisabled={availableSourceProperties.length === 0}
              onClick={() => handleAddField()}
              variant="outline"
            >
              Add another field
            </Button>
          </div>
          <div className="space-x-4">
            <Tooltip content="Attempt to automatically map fields based on their names">
              <Button
                variant="ghost"
                colorScheme="primary"
                icon={<WandSparklesIcon />}
                onClick={() => autoMapFields()}
                isDisabled={autoMappingSuggestions.length === 0}
              >
                Auto map{" "}
                {autoMappingSuggestions.length > 0
                  ? `(${autoMappingSuggestions.length})`
                  : ""}
              </Button>
            </Tooltip>
            {availableSourceProperties.length > 0 && (
              <Button
                variant="ghost"
                icon={<ListChecksIcon />}
                onClick={() => handleAddRemainingSourceProperties()}
              >
                Add remaining source columns ({availableSourceProperties.length}
                )
              </Button>
            )}
            <Button
              variant="ghost"
              colorScheme="danger"
              icon={<ListXIcon />}
              disabled={!canClearMappings()}
              onClick={async () => {
                const confirmed = await confirm({
                  title: "Unsaved Changes",
                  message: "Are you sure you want to clear all mappings?",
                  type: "danger",
                });
                if (confirmed) {
                  clearMappings();
                }
              }}
            >
              Clear all mappings
            </Button>
          </div>
        </ButtonGroup>
      </div>
    </>
  );
}
